import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Button } from '../buttons/button';
import { ProjectsIcon } from '../Icons/svgIcons';
import BaseModal from './BaseModal';
import { ICONS } from '../../constants/imageConstants';

const StartFormModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { forms } = useSelector(state => state.forms);
  const [selectedForm, setSelectedForm] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const filteredForms = useMemo(() => {
    if (!forms) return [];
    if (!searchTerm.trim()) return forms;
    const search = searchTerm.toLowerCase();
    return forms.filter(form => {
      const name = (form.title || form.name || '').toLowerCase();
      return name.includes(search);
    });
  }, [forms, searchTerm]);

  const handleContinue = () => {
    if (selectedForm) {
      onClose();
      navigate(`/forms/fill/${selectedForm}`);
    }
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="Start Form"
      subtitle="Select a form from the list to begin data collection."
      icon={ICONS.FORM_MODAL}
      iconBgColor="#E2E9FE"
    >
      <div className="space-y-4">
        <div className="relative">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search forms..."
            className="w-full h-11 pl-10 pr-4 bg-white border-2 border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all"
          />
          <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
            <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>

        <div className="space-y-2 max-h-[45vh] overflow-y-auto pr-1" style={{ scrollbarWidth: 'thin', scrollbarColor: '#cbd5e1 #f1f5f9' }}>
          {filteredForms.length === 0 ? (
            <div className="text-center py-10">
              <div className="w-14 h-14 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
                <svg className="w-7 h-7 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <p className="text-sm font-medium text-gray-600">
                {searchTerm ? 'No forms found' : 'No forms available'}
              </p>
              {searchTerm && (
                <p className="text-xs text-gray-500 mt-1">Try adjusting your search</p>
              )}
            </div>
          ) : (
            filteredForms.map((form) => (
              <button
                key={form.id}
                onClick={() => setSelectedForm(form.id)}
                className={`w-full px-3 py-2.5 rounded-lg border-2 transition-all duration-200 cursor-pointer ${
                  selectedForm === form.id
                    ? 'border-blue-500 bg-gradient-to-r from-blue-50 to-indigo-50 shadow-sm'
                    : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-sm'
                }`}
              >
                <div className="flex items-center gap-2.5">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-all ${
                    selectedForm === form.id
                      ? 'bg-gradient-to-br from-blue-100 to-indigo-100 border border-blue-200'
                      : 'bg-gray-50 border border-gray-100'
                  }`}>
                    <ProjectsIcon className={`w-4 h-4 ${selectedForm === form.id ? 'text-blue-600' : 'text-gray-500'}`} />
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <h4 className={`text-sm font-semibold truncate transition-colors ${
                      selectedForm === form.id ? 'text-blue-900' : 'text-gray-900'
                    }`}>
                      {form.title || form.name}
                    </h4>
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                    selectedForm === form.id
                      ? 'border-blue-600 bg-blue-600'
                      : 'border-gray-300'
                  }`}>
                    {selectedForm === form.id && (
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                </div>
              </button>
            ))
          )}
        </div>

        <Button 
          variant="primary" 
          fullWidth 
          size="large" 
          onClick={handleContinue}
          disabled={!selectedForm}
        >
          Continue
        </Button>
      </div>
    </BaseModal>
  );
};

export default StartFormModal;